/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import BaseObject from "@shared/BaseObject.js";
import {
	getPrimaryPersonOutlineRadius,
	isPrimaryPersonNodeData,
	PRIMARY_PERSON_HIGHLIGHT_COLOR,
	PRIMARY_PERSON_HIGHLIGHT_LINE_WIDTH,
	PRIMARY_PERSON_HIGHLIGHT_PADDING,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramPrimaryPersonHelper";
import {LEGEND_HOVER_DIM_CLASS} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendHoverHelper";

const PRIMARY_PERSON_DIM_OPACITY_MULTIPLIER = 0.35

export default class GenogramPrimaryPersonOutlineRenderer extends BaseObject {

	constructor({cy}) {
		super()
		this.cy = cy
		this.layer = null
		this.animationFrameId = null
		this._listeners = {
			nodeGeometryChanged: this.requestUpdate.bind(this),
			nodeResized: this.requestUpdate.bind(this),
			dataChanged: this.requestUpdate.bind(this),
			nodeClassChanged: this.requestUpdate.bind(this),
		}
		this.ensureLayer()
		this.addListeners()
	}

	addListeners() {
		this.cy.on("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.on("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.on("cyeditor.data", this._listeners.dataChanged)
		this.cy.on("class", "node", this._listeners.nodeClassChanged)
	}

	ensureLayer() {
		if(this.layer || !this.cy.layers){
			return
		}
		this.layer = this.cy.layers().nodeLayer.insertAfter("canvas")
		this.layer.callback((ctx) => {
			this.renderIntoContext(ctx)
		})
	}

	update() {
		this.layer?.update()
	}

	requestUpdate() {
		if(this.animationFrameId){
			return
		}
		if(typeof requestAnimationFrame !== "function"){
			this.update()
			return
		}
		this.animationFrameId = requestAnimationFrame(() => {
			this.animationFrameId = null
			this.update()
		})
	}

	destroy() {
		this.cy.off("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.off("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.off("cyeditor.data", this._listeners.dataChanged)
		this.cy.off("class", "node", this._listeners.nodeClassChanged)
		if(this.animationFrameId && typeof cancelAnimationFrame === "function"){
			cancelAnimationFrame(this.animationFrameId)
			this.animationFrameId = null
		}
		this.layer?.remove()
		this.layer = null
	}

	renderIntoContext(ctx) {
		let primaryNodes = this.getPrimaryPersonNodes()
		for(let node of primaryNodes){
			this.drawPrimaryPersonOutline(ctx, node)
		}
	}

	getPrimaryPersonNodes() {
		return this.cy.nodes().filter((node) => this.isRenderablePrimaryPersonNode(node))
	}

	isRenderablePrimaryPersonNode(node) {
		if(!node?.isNode()){
			return false
		}
		if(node.data("isAuxNode") || node.data("tempElement")){
			return false
		}
		if(node.hasClass("eh-handle") || node.hasClass("eh-ghost-node")){
			return false
		}
		return isPrimaryPersonNodeData(node.data())
	}

	drawPrimaryPersonOutline(ctx, node) {
		let box = node.boundingBox({
			includeLabels: false,
			includeOverlays: false,
		})
		let path = this.buildShapePath(node, box, PRIMARY_PERSON_HIGHLIGHT_PADDING)
		if(!path){
			return
		}

		ctx.save()
		if(node.hasClass(LEGEND_HOVER_DIM_CLASS)){
			ctx.globalAlpha *= PRIMARY_PERSON_DIM_OPACITY_MULTIPLIER
		}
		ctx.strokeStyle = PRIMARY_PERSON_HIGHLIGHT_COLOR
		ctx.lineWidth = PRIMARY_PERSON_HIGHLIGHT_LINE_WIDTH
		ctx.stroke(path)
		ctx.restore()
	}

	buildShapePath(node, box, offset) {
		let path = new Path2D()
		this.appendCirclePath(path, node, box, offset)
		return path
	}

	appendCirclePath(path, node, box, offset) {
		let centerX = box.x1 + box.w / 2
		let centerY = box.y1 + box.h / 2
		let radius = getPrimaryPersonOutlineRadius({
			shape: this.getNodeShape(node),
			width: box.w,
			height: box.h,
			offset,
			lineWidth: PRIMARY_PERSON_HIGHLIGHT_LINE_WIDTH,
		})
		path.ellipse(centerX, centerY, radius, radius, 0, 0, Math.PI * 2)
	}

	getNodeShape(node) {
		return node?.pstyle?.("shape")?.value || node?.data?.("shape") || "rectangle"
	}
}
